import { useCallback, useEffect, useState } from 'react';

/**
 * Dev toggle for the experimental octi **v2** skill-tree renderer.
 *
 * `?octiV2=1` on any dashboard URL seeds it ON (persisted to localStorage);
 * the returned setter flips it live (no reload) and persists. Only ever
 * consulted for octi plans — the four-track (cross) plans never see v2. Mirrors
 * `useDevTabsAccess` / `useActivePlan`; self-contained (no redux, no backend).
 */
const STORAGE_KEY = 'vega.dev.octiV2';
const PARAM = 'octiV2';

const readStored = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const resolveInitial = (): boolean => {
  try {
    const value = new URLSearchParams(window.location.search).get(PARAM);
    if (value === '0' || value === 'false') {
      window.localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    if (value !== null) {
      window.localStorage.setItem(STORAGE_KEY, '1');
      return true;
    }
  } catch {
    /* ignore quota / privacy-mode failures */
  }
  return readStored();
};

export function useOctiV2Access(): [boolean, (on: boolean) => void] {
  const [enabled, setEnabled] = useState<boolean>(resolveInitial);

  const set = useCallback((on: boolean) => {
    setEnabled(on);
    try {
      if (on) window.localStorage.setItem(STORAGE_KEY, '1');
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const sync = () => setEnabled(readStored());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  return [enabled, set];
}

/* ---- Tri-state octi renderer selector (v1 | v2 | v3) --------------------- */

export type OctiVersion = 'v1' | 'v2' | 'v3';
const VERSION_KEY = 'vega.dev.octiVersion';

const readVersion = (): OctiVersion => {
  try {
    const v = window.localStorage.getItem(VERSION_KEY);
    if (v === 'v2' || v === 'v3') return v;
  } catch {
    /* ignore */
  }
  return 'v1';
};

const resolveInitialVersion = (): OctiVersion => {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('octiV3') === '1') {
      window.localStorage.setItem(VERSION_KEY, 'v3');
      return 'v3';
    }
    if (q.get('octiV2') === '1') {
      window.localStorage.setItem(VERSION_KEY, 'v2');
      return 'v2';
    }
    if (q.get('octiV2') === '0' || q.get('octiV3') === '0') {
      window.localStorage.removeItem(VERSION_KEY);
      return 'v1';
    }
  } catch {
    /* ignore */
  }
  return readVersion();
};

/** Which octi renderer is active. `?octiV2=1` / `?octiV3=1` seed it; the setter
 *  flips live and persists. Consulted only for octi plans; cross never sees it. */
export function useOctiVersion(): [OctiVersion, (v: OctiVersion) => void] {
  const [version, setVersion] = useState<OctiVersion>(resolveInitialVersion);

  const set = useCallback((v: OctiVersion) => {
    setVersion(v);
    try {
      if (v === 'v1') window.localStorage.removeItem(VERSION_KEY);
      else window.localStorage.setItem(VERSION_KEY, v);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const sync = () => setVersion(readVersion());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  return [version, set];
}

export default useOctiV2Access;
